'use client';

import { useEffect, useRef, useState } from 'react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { MenuItem } from '@/features/header/NavList';

const TOP_ID = 'hero';
const HEADER_OFFSET = 90;

// Module-level: survives component remounts that would reset a useRef
let _pendingScrollTarget: string | null = null;

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useNavBehavior(items: MenuItem[]) {
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const locale = (params?.locale as string) ?? 'pt';
  const homePath = `/${locale}`;
  const onHome = pathname === homePath;

  const [activeId, setActiveId] = useState<string | null>(null);
  const lockedRef = useRef(false);
  const itemIds = items.map((i) => i.id).join('|');

  useEffect(() => {
    if (!onHome) {
      setActiveId(null);
      return;
    }

    const pickActive = () => {
      if (lockedRef.current) return;

      if (window.scrollY <= 8) {
        setActiveId(TOP_ID);
        return;
      }

      const line = HEADER_OFFSET + 2;
      // Keep the last nav item whose section has scrolled past the header line.
      // This ensures sections stay active even when extra blocks (e.g. BookBlock)
      // with different IDs are nested between two nav anchor points.
      const passed = items.filter((item) => {
        const el = document.getElementById(item.id);
        if (!el) return false;
        return el.getBoundingClientRect().top <= line;
      });
      const current = passed[passed.length - 1];

      setActiveId(current?.id ?? null);
    };

    // Retry loop to handle page transition delays
    let frames = 0;
    let rafId: number;
    const retry = () => {
      pickActive();
      if (frames++ < 20) rafId = requestAnimationFrame(retry);
    };
    rafId = requestAnimationFrame(retry);

    window.addEventListener('scroll', pickActive, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', pickActive);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHome, pathname, itemIds]);

  const isActive = (item: MenuItem) => {
    if (onHome) return activeId === item.id;
    if (item.id === TOP_ID) return pathname === homePath;
    if (!item.href.includes('#')) {
      return pathname === item.href || pathname.startsWith(`${item.href}/`);
    }
    const derivedRoute = `/${locale}/${item.id}`;
    return pathname === derivedRoute || pathname.startsWith(`${derivedRoute}/`);
  };

  useEffect(() => {
    if (!onHome || !_pendingScrollTarget) return;
    const targetId = _pendingScrollTarget;
    _pendingScrollTarget = null;

    const startTime = Date.now();
    const tryScroll = () => {
      const el = document.getElementById(targetId);
      if (el) {
        setActiveId(targetId);
        lockedRef.current = true;
        el.scrollIntoView({
          behavior: reducedMotion() ? 'instant' : 'smooth',
          block: 'start',
        });
        history.replaceState(null, '', `${homePath}#${targetId}`);
        setTimeout(() => {
          lockedRef.current = false;
        }, 1000);
        return;
      }
      if (Date.now() - startTime < 5000) {
        setTimeout(tryScroll, 50);
      }
    };
    requestAnimationFrame(tryScroll);
  }, [onHome, homePath]);

  const handleClick = (e: React.MouseEvent, item: MenuItem) => {
    if (!onHome) {
      if (item.href.includes('#')) {
        e.preventDefault();
        _pendingScrollTarget = item.id;
        router.push(homePath, { scroll: false });
      }
      return;
    }

    if (item.id === TOP_ID) {
      e.preventDefault();
      setActiveId(TOP_ID);
      window.scrollTo({
        top: 0,
        behavior: reducedMotion() ? 'instant' : 'smooth',
      });
      history.replaceState(null, '', homePath);
      return;
    }

    const targetEl = document.getElementById(item.id);
    if (!targetEl) return;

    e.preventDefault();
    setActiveId(item.id);
    lockedRef.current = true;
    targetEl.scrollIntoView({
      behavior: reducedMotion() ? 'instant' : 'smooth',
      block: 'start',
    });
    history.replaceState(null, '', item.href);

    setTimeout(() => {
      lockedRef.current = false;
    }, 1000);
  };

  return { getHref: (item: MenuItem) => item.href, isActive, handleClick };
}
